import { useTranslation } from 'react-i18next';
import { Form, FormItem, Spin } from '../Ant';
import TextAreaField from '../textAreaField/textAreaField';
import { Slash } from '../icons';
import { useLocation } from 'react-router-dom';
import { usePage } from '../../context/PageContext';
import { useEffect, useState } from 'react';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useMatterRequest } from '../../context/MatterRequestContext';
import { IMatterRequestParams } from '../../interface/matterRequest';
import { useCreateMatterRequest } from '../../hooks/useCreateMatterRequest';
import { ICustomerType } from '../../constants/customer';
import dayjs from 'dayjs';
import { useUser } from '../../context/UserContext';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useRestrictedUserMatterPermissions } from '../../hooks/useRestrictedUserMatterPermissions';
import { AnalyticsPage } from '../../interface/analytics';
import CustomModal from '../informationPopup/customModal';
import { FormCard } from '../../assets/styles/components/formCard.styled';
import PinnedSaveButton from '../pinnedSaveButton/pinnedSaveButton';

const CreateMatterRequest = () => {
  const { t } = useTranslation();
  const { goBack } = useNavigationStack();
  const location = useLocation();
  const { setTitle } = usePage();
  const { user } = useUser();
  const { trackPage } = useAnalytics();
  const { canCreateMatterRequests, isLoading: isPermissionLoading } =
    useRestrictedUserMatterPermissions();
  const createMatterRequestMutation = useCreateMatterRequest();
  const { matterRequestParams, updateMatterRequestParams } = useMatterRequest();
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [onDiscardConfirm, setOnDiscardConfirm] = useState<(() => void) | null>(null);

  useEffect(() => {
    trackPage(AnalyticsPage.CREATE_MATTER_REQUEST);
  }, [trackPage]);

  const handleBack = () => {
    goBack();
    updateMatterRequestParams(null);
  };
  const handleCreateMatterRequest = () => {
    const basePayload: IMatterRequestParams = {
      subject: matterRequestParams?.subject ?? '',
      description: matterRequestParams?.description ?? '',
      receivingDate: dayjs(new Date()).format('YYYY-MM-DD'),
      insider: false,
      responsibleUserId: user?.id ?? '',
      ...(matterRequestParams?.type === ICustomerType.COMPANY
        ? {
            principalCompanyId: matterRequestParams?.customerId,
          }
        : {
            principalPrivatePersonId: matterRequestParams?.customerId,
          }),
    };
    createMatterRequestMutation.mutate(
      {
        matterRequestData: basePayload,
      },
      {
        onSuccess: async () => {
          handleBack();
        },
      },
    );
  };
  const onBack = () => {
    validateOnFormChanged(() => {
      handleBack();
    });
  };
  const validateOnFormChanged = (onProceed: () => void) => {
    const isFormChanged = !!matterRequestParams?.subject || !!matterRequestParams?.description;
    setShowDiscardDialog(isFormChanged ?? false);
    setOnDiscardConfirm(() => onProceed);
    if (!isFormChanged) {
      onProceed?.();
    }
  };
  const handleDiscardConfirm = () => {
    onDiscardConfirm?.();
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };

  const handleDiscardCancel = () => {
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={onBack}
        showCross
        title={t('matterRequest:createMatterRequest')}
      />,
    );
  }, [matterRequestParams]);
  useEffect(() => {
    if (location?.state?.customerId) {
      updateMatterRequestParams({
        ...matterRequestParams,
        type: location?.state?.type,
        customerId: location?.state?.customerId,
      } as IMatterRequestParams);
    }
  }, []);
  useEffect(() => {
    if (isPermissionLoading || canCreateMatterRequests) return;
    goBack();
  }, [isPermissionLoading, canCreateMatterRequests]);

  if (isPermissionLoading) return <Spin fullscreen />;
  if (!canCreateMatterRequests) return null;

  return (
    <div className="h-full w-full pb-gutter add-transaction-form">
      <FormCard>
        <Form layout="vertical" className="w-full d-flex fd-column gap-gutter">
          <div className="relative w-full">
            <FormItem name="subject" label={t('common:subject')} required>
              <TextAreaField
                data-testid="subject-input"
                required={true}
                maxLength={250}
                placeholder={t('common:addSubject')}
                onChange={(e) => {
                  updateMatterRequestParams({
                    ...matterRequestParams,
                    subject: e.target.value,
                  } as IMatterRequestParams);
                }}
              />
            </FormItem>
          </div>
          <FormItem label={t('task:description')}>
            <TextAreaField
              data-testid="description-input"
              maxLength={1000}
              placeholder={t('common:addDescription')}
              onChange={(e) => {
                updateMatterRequestParams({
                  ...matterRequestParams,
                  description: e.target.value,
                } as IMatterRequestParams);
              }}
            />
          </FormItem>
          <PinnedSaveButton
            data-testid="create-button"
            disabled={!matterRequestParams?.subject}
            onClick={handleCreateMatterRequest}
          >
            {t('matterRequest:createMatterRequest')}
          </PinnedSaveButton>
        </Form>
      </FormCard>
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
    </div>
  );
};

export default CreateMatterRequest;
